import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Department, DepartmentPagedResponse } from '../../models/Department';
import { DepartmentService } from '../../services/department.service';
import { StaffService } from '../../services/staff.service';
import { StaffMember } from '../../models/StaffMember';
import { ErrorService } from '../../shared/services/error.service';
import { TableAction } from '../../shared/components/table/table.component';

@Component({
  selector: 'app-departments',
  templateUrl: './departments.component.html',
  styleUrls: ['./departments.component.css']
})
export class DepartmentsComponent implements OnInit {
  departments: Department[] = [];
  loading = false;
  error: string | null = null;

  searchTerm = '';
  
  // Controles de paginação
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  // Modal de atribuir gerente
  showAssignManagerModal = false;
  selectedDepartment: Department | null = null;
  assignManagerForm: FormGroup;
  staffMembers: StaffMember[] = [];
  isLoadingStaff = false;
  isSubmittingManager = false;

  columns: { titulo: string, label: string, formatter?: (value: any, item?: any) => string }[] = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Descrição', label: 'description' },
    { titulo: 'Gerente', label: 'managerStaffName', formatter: (value: any, item?: any) => this.formatManagerWithRegistration(value, item) },
    { titulo: 'Status', label: 'isActive', formatter: (value: any) => this.formatActiveStatus(value) },
    { titulo: 'Data Criação', label: 'createdAt', formatter: (value: any) => this.formatDate(value) }
  ];

  // Ações da tabela (base fixa, mas usaremos getActionsForDepartment para filtrar)
  actions: TableAction[] = [
    {
      label: 'Gerenciar Gerente',
      icon: 'fas fa-user-tie',
      action: 'assignManager',
      class: 'btn-primary'
    },
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'editDepartment',
      class: 'btn-warning'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'deleteDepartment',
      class: 'btn-danger'
    },
    {
      label: 'Cargos',
      icon: 'fas fa-briefcase',
      action: 'viewPositions',
      class: 'btn-info'
    }
  ];

  // Função para obter ações baseadas no departamento
  // Retorna ações sempre (permitir atribuir gerente mesmo quando não tem)
  getActionsForDepartment(department: Department): TableAction[] {
    // Sempre mostra a ação para permitir atribuir ou alterar gerente
    return this.actions;
  }

  // Verifica se o departamento selecionado tem gerente
  hasManager(): boolean {
    return !!(this.selectedDepartment && 
              this.selectedDepartment.managerStaffId !== null && 
              this.selectedDepartment.managerStaffId !== undefined);
  }

  constructor(
    private router: Router,
    private departmentService: DepartmentService,
    private staffService: StaffService,
    private fb: FormBuilder,
    private errorService: ErrorService
  ) {
    this.assignManagerForm = this.fb.group({
      managerStaffId: ['', Validators.required] // Obrigatório selecionar um gerente
    });
  }

  ngOnInit(): void {
    this.loadDepartments();
  }

  loadDepartments(): void {
    this.loading = true;
    this.error = null;

    this.departmentService.getDepartmentsPaginated(
      this.currentPage,
      this.pageSize,
      this.searchTerm
    ).subscribe({
      next: (response: DepartmentPagedResponse) => {
        if (!response) {
          this.error = 'Resposta inválida da API';
          this.loading = false;
          return;
        }

        if (!response.departments || !Array.isArray(response.departments)) {
          this.error = 'Formato de resposta inválido da API';
          this.loading = false;
          return;
        }

        this.departments = response.departments;
        this.totalItems = response.totalCount || 0;
        this.totalPages = response.totalPages || 0;
        this.currentPage = response.page || this.currentPage;
        this.pageSize = response.pageSize || this.pageSize;
        this.loading = false;
      },
      error: (error) => {
        this.error = error.message || 'Erro ao carregar departamentos';
        this.loading = false;
      }
    });
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.loadDepartments();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadDepartments();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadDepartments();
  }

  viewDepartment(department: Department): void {
    if (!department || !department.id) {
      this.error = 'Erro: ID de departamento inválido';
      return;
    }
    // TODO: Implementar navegação para detalhes do departamento
    // this.router.navigate(['/hr/departments', department.id]);
  }

  editDepartment(department: Department): void {
    if (!department || !department.id) {
      this.error = 'Erro: ID de departamento inválido';
      return;
    }

    this.router.navigate(['/hr/departments', department.id, 'edit']);
  }

  openAssignManagerModal(department: Department): void {
    this.selectedDepartment = department;
    this.loadStaffMembers();
    // Se já tem gerente, usar o ID; senão, deixar vazio
    const managerId = department.managerStaffId 
      ? (typeof department.managerStaffId === 'number' ? department.managerStaffId : parseInt(String(department.managerStaffId), 10))
      : '';
    this.assignManagerForm.patchValue({
      managerStaffId: managerId || ''
    });
    this.showAssignManagerModal = true;
  }

  closeAssignManagerModal(): void {
    this.showAssignManagerModal = false;
    this.selectedDepartment = null;
    this.assignManagerForm.reset();
  }

  loadStaffMembers(): void {
    this.isLoadingStaff = true;
    this.staffMembers = [];
    
    // Primeiro, buscar a primeira página para saber quantas páginas existem
    this.staffService.getStaffPaginated({
      page: 1,
      pageSize: 100,
      sortBy: 'FullName',
      sortDirection: 'asc'
    }).subscribe({
      next: (firstPageResponse) => {
        const allStaff: StaffMember[] = [...(firstPageResponse.staff || [])];
        const totalPages = firstPageResponse.totalPages || 1;
        
        // Se há mais páginas, buscar as restantes
        if (totalPages > 1) {
          let completedRequests = 1;
          
          for (let page = 2; page <= totalPages; page++) {
            this.staffService.getStaffPaginated({
              page: page,
              pageSize: 100,
              sortBy: 'FullName',
              sortDirection: 'asc'
            }).subscribe({
              next: (response) => {
                allStaff.push(...(response.staff || []));
                completedRequests++;
                
                // Se todas as páginas foram carregadas
                if (completedRequests === totalPages) {
                  this.staffMembers = allStaff;
                  this.isLoadingStaff = false;
                }
              },
              error: (error) => {
                completedRequests++;
                // Continuar mesmo com erro
                if (completedRequests === totalPages) {
                  this.staffMembers = allStaff;
                  this.isLoadingStaff = false;
                }
              }
            });
          }
        } else {
          // Apenas uma página
          this.staffMembers = allStaff;
          this.isLoadingStaff = false;
        }
      },
      error: (error) => {
        this.errorService.handleHttpError(error);
        this.isLoadingStaff = false;
      }
    });
  }

  onSubmitAssignManager(): void {
    if (this.assignManagerForm.invalid || !this.selectedDepartment) {
      this.assignManagerForm.markAllAsTouched();
      return;
    }

    this.isSubmittingManager = true;
    const managerStaffIdValue = this.assignManagerForm.get('managerStaffId')?.value;
    
    // Converter para número inteiro (obrigatório ter um gerente)
    let managerStaffId: number;
    if (typeof managerStaffIdValue === 'number') {
      managerStaffId = managerStaffIdValue;
    } else {
      const parsed = parseInt(String(managerStaffIdValue), 10);
      if (isNaN(parsed)) {
        this.isSubmittingManager = false;
        return;
      }
      managerStaffId = parsed;
    }

    // Manter as informações originais do departamento e apenas alterar o gerente
    const updateData = {
      name: this.selectedDepartment.name,
      description: this.selectedDepartment.description,
      managerStaffId: managerStaffId,
      isActive: this.selectedDepartment.isActive
    };

    this.departmentService.updateDepartment(this.selectedDepartment.id, updateData).subscribe({
      next: (response) => {
        this.isSubmittingManager = false;
        this.closeAssignManagerModal();
        this.loadDepartments(); // Recarregar lista
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isSubmittingManager = false;
      }
    });
  }

  onRemoveManager(): void {
    if (!this.selectedDepartment) {
      return;
    }

    // Confirmar ação
    if (!confirm('Tem certeza que deseja desatribuir o gerente deste departamento?')) {
      return;
    }

    this.isSubmittingManager = true;

    // Manter as informações originais do departamento e remover o gerente (null)
    const updateData = {
      name: this.selectedDepartment.name,
      description: this.selectedDepartment.description,
      managerStaffId: null,
      isActive: this.selectedDepartment.isActive
    };

    this.departmentService.updateDepartment(this.selectedDepartment.id, updateData).subscribe({
      next: (response) => {
        this.isSubmittingManager = false;
        this.closeAssignManagerModal();
        this.loadDepartments(); // Recarregar lista
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isSubmittingManager = false;
      }
    });
  }

  getManagerFieldError(): string {
    const field = this.assignManagerForm.get('managerStaffId');
    if (field && field.invalid && (field.touched || field.dirty)) {
      if (field.errors?.['required']) {
        return 'Selecione um gerente';
      }
    }
    return '';
  }

  isManagerFieldInvalid(): boolean {
    const field = this.assignManagerForm.get('managerStaffId');
    return !!(field && field.invalid && (field.touched || field.dirty));
  }

  deleteDepartment(department: Department): void {
    if (!department || !department.id) {
      this.error = 'Departamento inválido para exclusão.';
      return;
    }

    if (!confirm(`Tem certeza que deseja excluir o departamento "${department.name}"?`)) {
      return;
    }

    this.loading = true;
    this.error = null;

    this.departmentService.deleteDepartment(department.id).subscribe({
      next: () => {
        this.loadDepartments();
      },
      error: (error) => {
        this.error = 'Erro ao excluir departamento';
        this.loading = false;
        this.errorService.handleHttpError(error);
      }
    });
  }

  newDepartment(): void {
    this.router.navigate(['/hr/departments/new']);
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  formatActiveStatus(active: boolean): string {
    return active ? 'Ativo' : 'Inativo';
  }

  formatManager(manager: string | null): string {
    return manager || '-';
  }

  formatManagerWithRegistration(manager: string | null, item?: any): string {
    if (!manager) return '-';
    const registration = item?.managerStaffRegistration;
    if (registration) {
      return `${manager} (${registration})`;
    }
    return manager;
  }

  onActionClick(actionData: {action: string, item: any}): void {
    const { action, item } = actionData;
    
    switch (action) {
      case 'assignManager':
        this.openAssignManagerModal(item);
        break;
      case 'editDepartment':
        this.editDepartment(item);
        break;
      case 'deleteDepartment':
        this.deleteDepartment(item);
        break;
      case 'viewPositions':
        this.viewPositions(item);
        break;
      default:
        break;
    }
  }

  viewPositions(department: Department): void {
    this.router.navigate(['/hr/departments', department.id, 'positions']);
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    const sorted = [...this.departments].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
    this.departments = sorted;
  }

  exportToCSV(): void {
    const cols = this.columns;
    const header = cols.map(c => `"${c.titulo}"`).join(',');
    const rows = this.departments.map(item =>
      cols.map(c => {
        const raw = (item as any)[c.label];
        const val = c.formatter ? c.formatter(raw, item) : (raw ?? '');
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = '﻿' + [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'departamentos.csv';
    a.click();
    URL.revokeObjectURL(url);
  }
}

